import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { AddressModalComponent, AddressModalContext } from '../../address/address-modal.component';
import { Address } from '../../address/models/address.model';
import { AddressType } from '../../address/models/enums/address-type.enum';
import { Country } from '../../Foundation/models/enums/country.enum';
import { County } from '../../foundation/models/enums/county.enum';
import { Jurisdiction } from '../../foundation/models/enums/jurisdiction.enum';
import { BaseComponent } from '../../shared/base-components/base.component';
import { ValidationModel } from '../../shared/models/validation-results/validation.model';
import { ModalSize } from '../../shared/services/modal/mvd-modal.enum';
import { MvdModalService } from '../../shared/services/modal/mvd-modal.service';
import { CustomerContact } from '../models/customer-contact.model';
import { CustomerIdentification } from '../models/customer-identification.model';
import { CustomerName } from '../models/customer-name.model';
import { CustomerIndividualNameSuffixType } from '../models/enums/customer-individual-name-suffix-type.enum';
import { CustomerNameType } from '../models/enums/customer-name-type.enum';
import { CustomerOrganizationType } from '../models/enums/customer-organization-type.enum';
import { CustomerType } from '../models/enums/customer-type.enum';
import { SimpleCustomer } from '../models/views/simple-customer.model';
import { SimpleOrganization } from '../models/views/simple-organization.model';
import { CustomerManageService } from '../shared/customer-manage.service';
import { DuplicateCustomerContext, DuplicateCustomerModalComponent } from './add-customer-duplicate-modal.component';
import { toDateOrNull } from '@max/incident/shared/data-helpers';
import { CustomerIdentificationType } from '../models/enums/customer-identification-type.enum';

@Component({
    selector: 'add-customer',
    templateUrl: './add-customer.html'
})
export class AddCustomerComponent extends BaseComponent {
    @Input() customerTypeId;
    @Input() searchInfo;
    @Input() address: Address = new Address();
    @Input() showHeader = true;
    @Input() showButtonLoading = false;
    @Input() maskSSN = false;
    @Input() showCustomerType = true;
    @Input() isOverrideIndividualFormSubmit = false;
    @Output() onIndividualCustomerAdded: EventEmitter<number> = new EventEmitter<number>();
    @Output() onOrganizationCustomerAdded: EventEmitter<number> = new EventEmitter<number>();
    @Output() onCustomerCancel: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() onIndividualFormSubmit: EventEmitter<SimpleCustomer> = new EventEmitter<SimpleCustomer>();

    public form: UntypedFormGroup;
    public showEIN = true;
    public showAdditionalSection = false;
    public showIndividual = true;

    private returnData: any = {};
    public organizationTypeList: any[] = new CustomerOrganizationType().GetActive();
    private customerTypes: CustomerType = new CustomerType();
    public customerTypeList: any[] = new CustomerType().GetActive();
    public suffixTypes: any[] = new CustomerIndividualNameSuffixType().GetActive();
    private addressTypes: AddressType = new AddressType();
    private customerNameTypes: CustomerNameType = new CustomerNameType();
    private countries: Country = new Country();
    private counties: County = new County();
    private states: Jurisdiction = new Jurisdiction();
    private customerIdentificationType = new CustomerIdentificationType();

    constructor(
        public formBuilder: UntypedFormBuilder,
        public customerService: CustomerManageService,
        public modalService: MvdModalService,
        private _router: Router
    ) {
        super();
    }

    public ngOnInit(): void {
        this.form = this.formBuilder.group({
            customerTypeId: [{ value: 1, disabled: !this.showCustomerType }, Validators.required],
            firstName: [this.searchInfo.firstName],
            middleName: [this.searchInfo.middleName],
            lastName: [this.searchInfo.lastName],
            customerIndividualNameSuffixTypeId: [this.searchInfo.customerIndividualNameSuffixTypeId],
            dateOfBirth: [this.searchInfo.dateOfBirth],
            SSN: [this.searchInfo.customerIdentificationValue],
            organizationName: [this.searchInfo.organizationName],
            organizationTypeId: [null],
            EIN: [this.searchInfo.ein],
            OIN: [''],
            email: this.formBuilder.group({
                customerContactValue: [this.searchInfo.emailAddress],
                isCorrespondenceEnabled: [false],
                isPrimary: [true],
                isPreferredContactMethod: [false]
            }),
            cellPhone: this.formBuilder.group({
                customerContactValue: [this.searchInfo.phoneNumber],
                isCorrespondenceEnabled: [false],
                isPrimary: [true]
            })
        });

        this.updateCustomerValidators(1);
        this.form.get('customerTypeId').valueChanges.pipe(takeUntil(this.componentDestroyed))
            .subscribe(v => this.updateCustomerValidators(v));
    }

    public attestation = (): void => {
        this.customerService.generateOIN()
            .subscribe(result => {
                this.form.get('OIN').setValue(result.oin);
                this.showEIN = false;
            },
            e => this.handleValidationResult(e)
            );
    };

    public cancel = (): void => {
        this.onCustomerCancel.emit(true);
    };

    public saveIndividualCustomer = (override: boolean = false, index: number = 0): void => {
        if (this.form.valid && this.isOverrideIndividualFormSubmit) {
            let model = this.getIndividualModel();
            this.onIndividualFormSubmit.emit(model);
            return;
        }

        if (this.form.valid) {
            this.showButtonLoading = true;
            let model = this.getIndividualModel();
            model.override = override;
            this.customerTypeId = this.customerTypes.Individual.id;

            this.customerService.addIndividualCustomer(model)
                .subscribe(result => {
                    this.returnData = result.id;
                    this.showButtonLoading = false;
                    this.onIndividualCustomerAdded.emit(this.returnData);
                }, e => {
                    this.handleValidationResult(e);
                    this.showButtonLoading = false;
                });
        }
    };

    public handleValidationResult(error: HttpErrorResponse): void {
        if (error.status == 409) {
            this.showDuplicateModal(error.error);
            this.showValidationComponent = false;
        } else {
            const valResults = new ValidationModel(error.error);
            this.validationResults = valResults;
            this.showValidationComponent = true;
        }
    }

    public showDuplicateModal = (duplicates: any): void => {
        const data = new DuplicateCustomerContext(duplicates, this.customerTypeId);
        this.modalService.open(DuplicateCustomerModalComponent, data, ModalSize.Medium)
            .afterClosed()
            .subscribe(result => {
                if (result == 0) {
                    // Continue to add customer
                    if (this.customerTypeId == 1) {
                        this.saveIndividualCustomer(true);
                    } else {
                        this.saveOrganizationCustomer(true);
                    }
                } else if (result != null) {
                    // Result is customerNumberId
                    if (this.customerTypeId == 1) {
                        this._router.navigate(['/customer/individual', result]);
                    } else {
                        this._router.navigate(['/customer/organization', result]);
                    }
                }
            });
    };

    public saveOrganizationCustomer = (override: boolean = false, index: number = 0): void => {
        if (this.form.valid) {
            this.showButtonLoading = true;
            let model = this.getOrganizationModel();
            model.override = override;
            this.customerTypeId = this.customerTypes.Organization.id;
            model.name.customerNameTypeId = this.customerNameTypes.OrganizationName.id;
            this.customerService.addOrganizationCustomer(model)
                .subscribe({
                    next: result => this.returnData = result.id,
                    error: e => {
                        this.handleValidationResult(e);
                        this.showButtonLoading = false;
                    },
                    complete: () => {
                        this.showButtonLoading = false;
                        this.onOrganizationCustomerAdded.emit(this.returnData);
                    }
                });
        }
    };

    public deleteAddress = (): void => {
        this.address = null;
    };

    public selectIndividualAddress = (): void => {
        this.selectAddress('individual-customer');
    };

    public selectOrganizationAddress = (): void => {
        this.selectAddress('organization-customer');
    };

    private selectAddress = (componentName: string): void => {
        let data = new AddressModalContext(0, 0, componentName, true, false, null, null, this.address);
        this.modalService.open(AddressModalComponent, data)
            .afterClosed()
            .pipe(filter(result => !!result))
            .subscribe((a: Address) => {
                this.address = a;
                this.denormalizeAddress(this.address);
            });
    };

    public denormalizeAddress(address: Address) {
        if (this.form.get('customerTypeId').value == 1) {
            address.addressType = address.addressTypeId == this.addressTypes.Physical.id ? 'Residential' : 'Mailing';
        } else if (this.form.get('customerTypeId').value == 2) {
            address.addressType = address.addressTypeId == this.addressTypes.Physical.id ? 'Commercial' : 'Mailing';
        }
        address.country = this.getValues(address.countryId, this.countries);
        address.county = this.getValues(address.countyId, this.counties);
        address.state = this.getValues(address.jurisdictionId, this.states);
    }

    public getValues = (id: number, types: any): string => {
        if (id !== undefined && id !== null) {
            try {
                return types.FromId(id).displayValue;
            } catch (exception) {
                return '';
            }
        } else {
            return '';
        }
    };

    private getIndividualModel = (): SimpleCustomer => {
        let model = new SimpleCustomer();
        // model.id;
        model.individualAddress = this.address || <Address> {};
        model.dateOfBirth = toDateOrNull(this.form.get('dateOfBirth').value);
        model.name = new CustomerName();
        model.name.lastName = this.form.get('lastName').value;
        model.name.middleName = this.form.get('middleName').value;
        model.name.firstName = this.form.get('firstName').value;
        model.name.customerIndividualNameSuffixTypeId = this.form.get('customerIndividualNameSuffixTypeId').value;
        model.ssn = new CustomerIdentification();
        model.ssn.customerIdentificationTypeId = this.customerIdentificationType.SSN.id;
        model.ssn.customerIdentificationValue = this.form.get('SSN').value;
        model.personalCell = new CustomerContact();
        model.personalCell.customerContactValue = this.form.get('cellPhone.customerContactValue').value?.trim();
        model.personalCell.isCorrespondenceEnabled = this.form.get('cellPhone.isCorrespondenceEnabled').value;
        model.personalCell.isPrimary = this.form.get('cellPhone.isPrimary').value;
        model.personalEmail = new CustomerContact();
        model.personalEmail.customerContactValue = this.form.get('email.customerContactValue').value?.trim();
        model.personalEmail.isCorrespondenceEnabled = this.form.get('email.isCorrespondenceEnabled').value;
        model.personalEmail.isPrimary = this.form.get('email.isPrimary').value;
        model.personalEmail.isPreferredContactMethod = this.form.get('email.isPreferredContactMethod').value;

        return model;
    };

    private getOrganizationModel = (): SimpleOrganization => {
        let model = new SimpleOrganization();

        model.name = new CustomerName();
        model.organizationAddress = this.address || <Address> {};
        model.name.organizationName = this.form.get('organizationName').value;
        model.ein = new CustomerIdentification();
        model.ein.customerIdentificationTypeId = this.customerIdentificationType.EIN.id;
        model.ein.customerIdentificationValue = this.form.get('EIN').value;
        model.oin = new CustomerIdentification();
        model.oin.customerIdentificationTypeId = this.customerIdentificationType.OtherIdentificationNumber.id;
        model.oin.customerIdentificationValue = this.form.get('OIN').value;
        model.businessCell = new CustomerContact();
        model.businessCell.customerContactValue = this.form.get('cellPhone.customerContactValue').value;
        model.businessCell.isCorrespondenceEnabled = this.form.get('cellPhone.isCorrespondenceEnabled').value;
        model.businessCell.isPrimary = this.form.get('cellPhone.isPrimary').value;
        model.businessEmail = new CustomerContact();
        model.businessEmail.customerContactValue = this.form.get('email.customerContactValue').value;
        model.businessEmail.isCorrespondenceEnabled = this.form.get('email.isCorrespondenceEnabled').value;
        model.businessEmail.isPrimary = this.form.get('email.isPrimary').value;
        model.businessEmail.isPreferredContactMethod = this.form.get('email.isPreferredContactMethod').value;

        return model;
    };

    private updateCustomerValidators = (customerTypeId: number): void => {
        this.showIndividual = 1 == customerTypeId;

        if (true == this.showIndividual) {
            this.form.get('firstName').setValidators(Validators.maxLength(50));
            this.form.get('middleName').setValidators(Validators.maxLength(50));
            this.form.get('lastName').setValidators([Validators.required, Validators.maxLength(70)]);
            this.form.get('organizationName').clearValidators();
        } else {
            this.form.get('firstName').clearValidators();
            this.form.get('middleName').clearValidators();
            this.form.get('lastName').clearValidators();
            this.form.get('organizationName').setValidators([Validators.required, Validators.maxLength(256)]);
        }

        this.form.get('firstName').updateValueAndValidity();
        this.form.get('middleName').updateValueAndValidity();
        this.form.get('lastName').updateValueAndValidity();
        this.form.get('organizationName').updateValueAndValidity();
    };
}
